import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bookmark, ChevronDown, Trash2, Users, Check } from 'lucide-react';
import { getSavedViews, createSavedView, deleteSavedView } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import './SavedViews.css';

// A list page's filters, search and sort under a name. The page says what a
// view holds (`current`) and how to apply one (`onApply`); this keeps the
// list, per person, with an admin able to share one with everyone.

export default function SavedViews({ entityType, current, onApply }) {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [share, setShare] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const isAdmin = user && user.role_name === 'Admin';

  const views = useQuery({ queryKey: ['saved_views', entityType], queryFn: () => getSavedViews(entityType).then((r) => r.data), staleTime: 60 * 1000 });
  const create = useMutation({
    mutationFn: (body) => createSavedView(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['saved_views', entityType] }),
  });
  const remove = useMutation({
    mutationFn: (id) => deleteSavedView(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['saved_views', entityType] }),
  });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setNaming(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => { if (naming && inputRef.current) inputRef.current.focus(); }, [naming]);

  const list = views.data || [];

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ entity_type: entityType, name: name.trim(), config: current, is_shared: share });
      toast.success(`Saved view “${name.trim()}”`);
      setName(''); setShare(false); setNaming(false);
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.error) || 'Could not save the view');
    }
  };

  const apply = (view) => {
    onApply(view.config || {});
    setOpen(false);
    toast.info(`Showing “${view.name}”`);
  };

  const del = async (view) => {
    try { await remove.mutateAsync(view.id); toast.success(`Removed “${view.name}”`); }
    catch (err) { toast.error('Could not remove the view'); }
  };

  return (
    <div className="saved-views" ref={ref}>
      <button
        type="button"
        className={`btn btn-secondary ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Saved views"
      >
        <Bookmark size={16} aria-hidden="true" /> Views{list.length ? <span className="saved-views-count">{list.length}</span> : null} <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className="saved-views-menu" role="menu" aria-label="Saved views">
          {list.length === 0 && !naming && <p className="saved-views-empty">No saved views yet. Set the filters you want, then save them under a name.</p>}
          {list.map((view) => (
            <div key={view.id} className="saved-views-item">
              <button type="button" role="menuitem" className="saved-views-apply" onClick={() => apply(view)}>
                <span className="saved-views-name">{view.name}</span>
                {view.is_shared && <span className="saved-views-shared" title={`Shared by ${view.owner_name || 'an admin'}`}><Users size={11} aria-hidden="true" /> shared</span>}
              </button>
              {(view.mine || isAdmin) && (
                <button type="button" className="btn btn-icon btn-sm" onClick={() => del(view)} aria-label={`Remove view “${view.name}”`} title="Remove"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
          <div className="saved-views-footer">
            {naming ? (
              <form className="saved-views-form" onSubmit={save}>
                <input ref={inputRef} type="text" className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this view" aria-label="View name" maxLength={80} />
                {isAdmin && (
                  <label className="saved-views-share"><input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} /> Share with everyone</label>
                )}
                <div className="saved-views-actions">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim() || create.isPending}><Check size={13} /> Save</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setNaming(false); setName(''); }}>Cancel</button>
                </div>
              </form>
            ) : (
              <button type="button" role="menuitem" className="btn btn-secondary btn-sm" onClick={() => setNaming(true)}>Save current view…</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
